import { Group, SelectProps, Stack } from '@mantine/core';
import take from 'lodash/take';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';

import { Select } from '@/components/Select';
import { ColorInputPanel } from '@/components/Theme/ColorInputPanel';
import { ResetButton } from '@/components/Theme/ResetButton';
import { SwitchPanel } from '@/components/Theme/SwitchPanel';
import { TooltipConfigProps } from '@/utils/tiptap/extensions/Tooltip/components/TooltipConfig';
import { TOOLTIP_SHADOWS, extractShadowColor } from '@/utils/tiptap/extensions/Tooltip/utils/tooltipUtils';
import { FormControl } from '@/widgets/_components/FormControl';

export function Shadow(props: TooltipConfigProps) {
  const { styles, onRemoveStyle, resolvedStyles, onUpdateStyle } = props;
  const { t } = useTranslation('components', { keyPrefix: 'theme.settings' });

  const shadowSettings = take(resolvedStyles.shadow?.split(' '), 4);
  const shadowColor = extractShadowColor(resolvedStyles.shadow);

  const setShadow = useCallback(
    (newShadow: string) => {
      onUpdateStyle({ shadow: newShadow });
    },
    [onUpdateStyle]
  );

  const setShadowSettings = useCallback(
    (newSettings: string) => {
      setShadow(`${newSettings} ${shadowColor}`);
    },
    [setShadow, shadowColor]
  );

  const setShadowColor = useCallback(
    (newColor: string) => {
      setShadow(`${shadowSettings.join(' ')} ${newColor}`);
    },
    [setShadow, shadowSettings]
  );

  return (
    <FormControl title={t('shadow')}>
      <Stack>
        <SwitchPanel
          value={resolvedStyles.isShadowVisible}
          onChange={isShadowVisible => onUpdateStyle({ isShadowVisible })}
          ResetButton={'isShadowVisible' in styles && <ResetButton onClick={() => onRemoveStyle('isShadowVisible')} />}
        />
        <Group>
          <Select
            label={t('style')}
            data={TOOLTIP_SHADOWS}
            value={shadowSettings.join(' ')}
            onChange={setShadowSettings as SelectProps['onChange']}
          />
          <ColorInputPanel label={t('color')} color={shadowColor} setColor={setShadowColor} />
          {styles.shadow && <ResetButton onClick={() => onRemoveStyle('shadow')} />}
        </Group>
      </Stack>
    </FormControl>
  );
}
